import React from 'react';
import { EngineIcon, engineLabels } from 'Utils';
import { Badge, Button, Form } from 'react-bootstrap';
import { BsGearFill, BsPlayFill, BsCloud } from 'react-icons/bs';
import RDSIcon from 'react-aws-icons/dist/aws/logo/RDS';
import EC2Icon from 'react-aws-icons/dist/aws/logo/EC2';
import S3Icon from 'react-aws-icons/dist/aws/logo/S3';
import { FaServer, FaCloud, FaAws, FaDatabase } from 'react-icons/fa';
import { IoTimerOutline } from 'react-icons/io5';
import {
  SiMicrosoftazure,
  SiGooglecloud,
} from 'react-icons/si';
import { AiFillDatabase } from 'react-icons/ai';
import { VscServer } from 'react-icons/vsc';
import { inferCloud, kindForSource } from '../LandscapeUtils';
import './Components.css';

const PROVIDER_META = {
  aws: { label: 'AWS', Icon: FaAws, color: '#ff9900' },
  azure: { label: 'Azure', Icon: SiMicrosoftazure, color: '#0078D4' },
  gcp: { label: 'GCP', Icon: SiGooglecloud, color: '#4285F4' },
};

const READ_ONLY_SOURCES = new Set(['s3', 'azure_storage', 'azure_blob', 'gcs']);

const ManageButton = (props) => {
  let rowData = props.rowData;

  const readOnly = READ_ONLY_SOURCES.has(rowData.source)
    || kindForSource(rowData.source) === 'files'
    || rowData.connectionType === 's3';

  let label = rowData.status;
  if (rowData.status === 'running' || readOnly) label = 'available';
  let checked = label === 'available' || label === 'starting';

  let toggleMode = rowData.status;
  if (readOnly) toggleMode = 'static';
  if (rowData.engineMode === 'serverless') toggleMode = 'serverless';
  if (toggleMode === 'running') toggleMode = 'available';
  if (toggleMode === 'pending') {
    label = 'starting';
    toggleMode = 'starting';
  }

  if (props.fetchingData) {
    toggleMode = 'refreshing';
    label = 'refreshing';
  }

  return (
    <div className={'form-check form-check-inline reverse ' + toggleMode + '-resource'}>
      {!props.fetchingData ? (
        <Form.Check
          id={rowData.identifier + '-manage-toggle'}
          onChange={() => {}}
          type='switch'
          size='lg'
          checked={checked}
        />
      ) : (
        <IoTimerOutline size={'1rem'} className='mr-1' />
      )}
      <label className='form-check-label' htmlFor={rowData.identifier + '-manage-toggle'}>
        {' '}
        {label}{' '}
      </label>
    </div>
  );
};

const SettingsButton = ({ showModal, host }) => (
  <Button variant='outline-secondary rounded' onClick={() => showModal(host)}>
    <BsGearFill />
  </Button>
);

const ActionButton = () => (
  <Button variant='outline-primary rounded' onClick={() => console.log('button')}>
    <BsPlayFill color='#fff' />
  </Button>
);

const RowInput = ({ field, rowData, type }) => {
  const value = rowData[field];
  const handleInputChange = rowData.handleInputChange;
  return (
    <Form.Control
      onChange={(e) => handleInputChange(field, e.target.value)}
      value={value}
      type={type || 'text'}
      size='sm'
      disabled={rowData.engine === 's3'}
    />
  );
};

const sourceIcons = {
  rds: <RDSIcon size='24px' />,
  ec2: <EC2Icon size='24px' />,
  s3: <S3Icon size='24px' />,
  azure_sql: <AiFillDatabase size='22px' color='#0078D4' />,
  azure_sql_mi: <AiFillDatabase size='22px' color='#0078D4' />,
  azure_postgres: <AiFillDatabase size='22px' color='#0078D4' />,
  azure_mysql: <AiFillDatabase size='22px' color='#0078D4' />,
  azure_oracle: <AiFillDatabase size='22px' color='#0078D4' />,
  azure_synapse: <AiFillDatabase size='22px' color='#0078D4' />,
  azure_vm: <SiMicrosoftazure size='22px' color='#0078D4' />,
  azure_storage: <BsCloud size='22px' color='#0078D4' />,
  azure_blob: <BsCloud size='22px' color='#0078D4' />,
  gcp_sql: <FaDatabase size='22px' color='#4285F4' />,
  gce_vm: <VscServer size='22px' color='#EA4335' />,
  gcs: <FaCloud size='22px' color='#4285F4' />,
  external: <FaServer size='24px' />,
};

const sourceLabels = {
  rds: 'RDS',
  ec2: 'EC2',
  s3: 'S3',
  azure_sql: 'Azure SQL',
  azure_sql_mi: 'Azure SQL MI',
  azure_postgres: 'Azure PostgreSQL',
  azure_mysql: 'Azure MySQL',
  azure_oracle: 'Azure Oracle',
  azure_synapse: 'Azure Synapse',
  azure_vm: 'Azure VM',
  azure_storage: 'Azure Storage',
  azure_blob: 'Blob',
  gcp_sql: 'Cloud SQL',
  gce_vm: 'GCE',
  gcs: 'GCS',
};

// Per-cloud generic icon by category, used when a source key has no explicit
// entry above — so any new `<cloud>_<engine>` key still renders a sensible icon.
const genericIconFor = (source) => {
  const cloud = inferCloud(source);
  const kind = kindForSource(source);
  const color = (PROVIDER_META[cloud] || {}).color || '#666';
  if (kind === 'files') return <BsCloud size='22px' color={color} />;
  if (kind === 'servers') return <FaServer size='22px' color={color} />;
  return <FaDatabase size='22px' color={color} />;
};

// Humanize an unknown source key, e.g. "azure_sql_mi" -> "AZURE SQL MI".
const humanizeSource = (source) => String(source || '').replace(/_/g, ' ');

const SourceColumn = ({ source }) => (
  <div>
    <span className='icon-span mr-1'>{sourceIcons[source] || genericIconFor(source)}</span>
    <span className='icon-span text-uppercase'>{sourceLabels[source] || humanizeSource(source)}</span>
  </div>
);

const CLOUD_BADGE_VARIANTS = {
  aws: 'warning',
  azure: 'info',
  gcp: 'primary',
};

const CloudBadge = ({ cloud }) => {
  const key = (cloud || 'aws').toLowerCase();
  const meta = PROVIDER_META[key] || { label: key.toUpperCase(), Icon: null };
  const variant = CLOUD_BADGE_VARIANTS[key] || 'secondary';
  const Icon = meta.Icon;
  return (
    <Badge
      variant={variant}
      style={{
        fontSize: '11px',
        padding: '4px 8px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
      }}
      title={meta.label}
    >
      {Icon ? <Icon size={11} /> : null}
      <span>{meta.label}</span>
    </Badge>
  );
};

const CloudColumn = ({ cloud }) => <CloudBadge cloud={cloud} />;

const EngineColumn = ({ engine }) => (
  <div className='text-uppercase'>
    <div className='d-flex align-items-center'>
      {engine ? (
        <span>
          <span className='icon-span mr-1'>
            <EngineIcon engine={engine} />
          </span>
          <span className='text-span'>{engineLabels[engine] || engine}</span>
        </span>
      ) : (
        <span>-</span>
      )}
    </div>
  </div>
);

export {
  ActionButton,
  EngineColumn,
  SourceColumn,
  ManageButton,
  RowInput,
  CloudBadge,
  CloudColumn,
  sourceIcons,
  sourceLabels,
  READ_ONLY_SOURCES,
};
